import axios from 'axios';

/**
 * Manejo global de 401. Se importa una sola vez desde index.js (efecto de
 * módulo, corre antes de que se monte <App />).
 *
 * Sin esto, cuando el token expiraba o dejaba de ser válido (ej. sesión de
 * otro municipio — ver `MUNICIPIO_MISMATCH` en el backend), cada componente
 * cacheaba el error por su cuenta y mostraba un cartel rojo genérico: el
 * usuario quedaba en una pantalla rota sin entender que tenía que volver a
 * loguearse. Ahora, ante cualquier 401 de un request hecho con sesión
 * activa, se limpia el estado local y se manda a /login.
 *
 * Nota: la descarga de informes de Reportes usa `fetch()` (no axios) para
 * poder mandar el header en una descarga binaria, así que ese request no pasa
 * por el interceptor: Reportes llama a mano a cerrarSesionSiCorresponde() (más
 * abajo, la misma regla) cuando la respuesta no es ok.
 *
 * Mismo tratamiento para el 403 de un municipio suspendido o dado de baja
 * (ver middlewareResolverMunicipio en el backend): corta el acceso de
 * inmediato, también a las sesiones ya abiertas, y el usuario quedaba en una
 * pantalla con carteles rojos genéricos. Acá se lo manda a /login con el
 * mensaje del backend, que Login.jsx muestra en su cartel rojo. Se distingue
 * por el `codigo` y NO por el status: permitirRoles también devuelve 403
 * (por permisos) y eso no tiene que cerrar la sesión.
 *
 * Los requests a /api/superadmin/* quedan afuera: son otro realm de auth
 * (token propio bajo "superadmin_token", ver pages/Superadmin.jsx). Un 401
 * ahí lo maneja ese componente — no debe tocar la sesión de un municipio.
 */

// Dónde se deja el mensaje del 403 para que Login.jsx lo muestre al llegar
// (sessionStorage: sobrevive al replace('/login') y muere con la pestaña).
export const CLAVE_MENSAJE_ACCESO = 'mensaje_acceso_municipio';

const CODIGOS_MUNICIPIO_SIN_ACCESO = ['MUNICIPIO_SUSPENDIDO', 'MUNICIPIO_INACTIVO'];

// Invitados demo de tumunicipio (panel de Superadmin → Demos): una demo vencida
// o revocada con la sesión abierta recibe 401 (de verificarToken) o 403, con
// estos códigos. Se trata igual que el municipio sin acceso: se cierra la sesión
// y se muestra el motivo en el login.
// DEMO_SIN_PERMISO queda AFUERA a propósito: es un 403 por intentar una acción
// que a un invitado demo no le corresponde (gestionar el personal, borrar un
// diseño), no un problema de sesión. Cerrarle la sesión por tocar un botón
// sería un mal trato en plena demo; el mensaje se ve en el cartel de error de
// cada pantalla. Si algún día se prefiere cerrarla, alcanza con sumarlo acá.
const CODIGOS_DEMO_SIN_ACCESO = ['DEMO_VENCIDA', 'DEMO_REVOCADA'];

// Personal de municipio desactivado o dado de baja con la sesión abierta: verificarToken responde 401 con este
// `codigo` (el JWT dura 12 h, pero el backend revisa que la cuenta siga activa). Se cierra la sesión y el login
// muestra el motivo. Si el mensaje del backend no llegara, se usa este.
const CODIGO_USUARIO_INACTIVO = 'USUARIO_INACTIVO';
export const MENSAJE_USUARIO_INACTIVO = 'Tu cuenta fue desactivada. Comunicate con el administrador de tu municipio.';

// Sesión revocada: se restableció la contraseña o el 2FA de esa cuenta (el backend compara la versión de sesión del token
// con la de la base, migración 011) → 401 SESION_REVOCADA. Se cierra la sesión y el login muestra el motivo.
const CODIGO_SESION_REVOCADA = 'SESION_REVOCADA';
export const MENSAJE_SESION_REVOCADA = 'Tu sesión se cerró por seguridad. Iniciá sesión de nuevo.';

/**
 * Decide si una respuesta de error (de axios o de un fetch() suelto) significa "esta sesión ya no sirve" y, si es así,
 * la cierra, deja el motivo para el login y manda a /login. Es lo que usa el interceptor de abajo; las pocas llamadas
 * que no pasan por axios (la descarga de informes de Reportes) la llaman a mano con su status y su cuerpo.
 * Devuelve true si cerró la sesión.
 */
export function cerrarSesionSiCorresponde({ status, data, url = '' }) {
  const es401 = status === 401;
  const esMunicipioSinAcceso = status === 403 && CODIGOS_MUNICIPIO_SIN_ACCESO.includes(data?.codigo);
  const esDemoSinAcceso = (status === 401 || status === 403) && CODIGOS_DEMO_SIN_ACCESO.includes(data?.codigo);
  const esUsuarioInactivo = status === 401 && data?.codigo === CODIGO_USUARIO_INACTIVO;
  const esSesionRevocada = status === 401 && data?.codigo === CODIGO_SESION_REVOCADA;
  const habiaSesion = !!sessionStorage.getItem('token');
  const esRutaSuperadmin = url.includes('/superadmin/');

  if (!((es401 || esMunicipioSinAcceso || esDemoSinAcceso) && habiaSesion && !esRutaSuperadmin)) return false;

  sessionStorage.removeItem('token');
  sessionStorage.removeItem('usuario');
  if ((esMunicipioSinAcceso || esDemoSinAcceso) && data?.mensaje) {
    sessionStorage.setItem(CLAVE_MENSAJE_ACCESO, data.mensaje);
  } else if (esUsuarioInactivo) {
    sessionStorage.setItem(CLAVE_MENSAJE_ACCESO, data?.mensaje || MENSAJE_USUARIO_INACTIVO);
  } else if (esSesionRevocada) {
    sessionStorage.setItem(CLAVE_MENSAJE_ACCESO, data?.mensaje || MENSAJE_SESION_REVOCADA);
  }
  // replace (no href): no deja la pantalla rota en el historial del
  // navegador. El guard evita un reload inútil si ya estamos en /login.
  if (window.location.pathname !== '/login') {
    window.location.replace('/login');
  }
  return true;
}

axios.interceptors.response.use(
  (respuesta) => respuesta,
  (error) => {
    if (error.response) {
      cerrarSesionSiCorresponde({
        status: error.response.status,
        data: error.response.data,
        url: error.config?.url || '',
      });
    }
    return Promise.reject(error);
  }
);
